import React, { useState, useContext, createContext, ReactNode, useEffect } from 'react';
import { AuthProvider, useAuth } from './Auth';
import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom';
import "./styles/NavBar.css";

interface NavItemProps {
  to: string;
  children: ReactNode;
}

// Simple link component (simulating React Router Link)
const NavLink: React.FC<NavItemProps> = ({ to, children }) => (
  <Link
    to={to}
    className="nav-link"
  >
    {children}
  </Link>
);

// Dropdown link component
const DropdownLink: React.FC<NavItemProps> = ({ to, children }) => (
  <Link
    to={to}
    className="dropdown-link"
  //onClick={(e) => { e.preventDefault(); console.log(`Navigating to ${to}`); }}
  >
    {children}
  </Link>
);

// The main navigation bar component
const NavBar: React.FC = () => {
  const { user, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false); // Mobile menu state
  const [isPortfolioOpen, setIsPortfolioOpen] = useState(false); // Desktop dropdown state

  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  // Portfolio dropdown items
  const portfolioItems = [
    { name: 'Portrait', to: '/portfolio/portrait' },
    { name: 'Street', to: '/portfolio/street' },
    { name: 'Performance', to: '/portfolio/performance' },
    { name: 'Event', to: '/portfolio/event' },
    { name: 'Landscape', to: '/portfolio/landscape' },
    { name: 'Cityscape', to: '/portfolio/cityscape' },
  ];

  const baseLinks = [
    { name: 'About', to: '/about' },
    { name: 'Contact', to: '/contact' },
  ];

  return (
    <nav className="navbar-container">
      <div className="navbar-inner-wrapper">
        <div className="navbar-flex-container">

          {/* Logo / Brand Name */}
          <div className="logo-container">
            <NavLink key="Home" to="/"> <div className="brand-logo"> DSW Photography</div></NavLink>
          </div>

          {/* Desktop Navigation Links */}
          <div className="desktop-nav-links">


            {/* Portfolio Dropdown (using React mouse events for hover) */}
            <div
              className="portfolio-dropdown-container"
              onMouseEnter={() => setIsPortfolioOpen(true)}
              onMouseLeave={() => setIsPortfolioOpen(false)}
            >
              <span className="nav-link nav-link-item">
                Portfolio ▾
              </span>

              {/* Dropdown Menu Content */}
              <div className={`dropdown-menu ${isPortfolioOpen ? 'is-visible' : ''}`}>
                <div className="dropdown-menu-content">
                  {portfolioItems.map(item => (
                    <DropdownLink key={item.name} to={item.to}>{item.name}</DropdownLink>
                  ))}
                </div>
              </div>
            </div>

            {/* Standard Links */}
            {baseLinks.map(link => (
              <NavLink key={link.name} to={link.to}>{link.name}</NavLink>
            ))}
            {user && (
              <NavLink to="/projects" >Projects</NavLink>
            )}

            {/* Conditional Links for Permissions */}
            {user?.UserType === 'admin' && (
              <>
                <NavLink to="/upload" >Upload</NavLink>
                <NavLink to="/browse" >Browse</NavLink>
                <NavLink to="/accounts" >Accounts</NavLink>
              </>
            )}
          </div>

          {/* Account Management Placeholder */}
          {user ? <span className='nav-link' onClick={logout}>Logout</span> : <NavLink key={'login'} to="/login">Login</NavLink>}

          {/* Mobile Menu Button */}
          <div className="mobile-menu-button-wrapper">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="mobile-menu-button"
            >
              <svg className="menu-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>


      {/* Mobile Menu Content (Conditionally rendered) */}
      <div
        className={`mobile-menu-content ${isMenuOpen ? 'is-open' : ''}`}
        aria-expanded={isMenuOpen}
      >
        <div className="mobile-link-list">
          {baseLinks.map(link => (
            <NavLink key={link.name} to={link.to}>{link.name}</NavLink>
          ))}

          {/* Portfolio Dropdown for Mobile (always expanded for simplicity) */}
          <div className="mobile-portfolio-section">
            <span className="nav-link nav-link-item">
              Portfolio
            </span>
            <div className="mobile-dropdown-list">
              {portfolioItems.map(item => (
                <DropdownLink key={item.name} to={item.to}>{item.name}</DropdownLink>
              ))}
              {user && (
                <NavLink to="/projects" >Projects</NavLink>
              )}
              {/* Conditional Links for Mobile */}
              {user?.UserType === 'admin' && (
                <>
                  <NavLink to="/upload" >Upload</NavLink>
                  <NavLink to="/browse" >Browse</NavLink>
                  <NavLink to="/accounts" >Accounts</NavLink>
                </>
              )}


            </div>
          </div>

        </div>
      </div>
    </nav>
  );
};

export default NavBar;